import { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  EyeIcon,
  GlobeAltIcon,
  ScaleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  ArrowsPointingInIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FadeIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ScaleIn({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-40px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */
const capabilities = [
  {
    icon: EyeIcon,
    title: 'Marketing Compliance Monitoring',
    description:
      'Every ad, email, social post, and disclosure checked against federal regulatory rules — Reg Z, UDAAP, Reg DD, ECOA, Fair Housing Act, and more. AI-powered detection with deterministic rules engine verification and mandatory human approval workflows. Examiner-ready audit trail on every check.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'HMDA LAR Builder & Validation',
    description:
      'Validate, clean, and prepare LAR data for submission. 170+ CFPB edit checks across syntactical, validity, quality, and macro-quality categories with plain-language AI explanations. Field mapping templates for LOS imports. Data quality scoring, geocoding validation, and submission-ready exports.',
  },
  {
    icon: ScaleIcon,
    title: 'Fair Lending Analysis',
    description:
      'Full IFLEP-aligned Fair Lending Dashboard with 17 risk factors across underwriting, pricing, steering, redlining, and marketing. Denial rate and pricing disparity analysis with chi-square, Fisher\'s exact, and Student\'s t-test statistical testing. Census tract mapping with demographic overlays and majority-minority tract classification. Content screening for prohibited basis language, steering, and discouragement patterns.',
  },
  {
    icon: ChartBarIcon,
    title: 'Regression Analysis',
    description:
      'Logistic regression for denial and steering models. OLS regression for pricing disparity analysis. Matched-pair analysis for examiner file review. Regulator-aware output formatted for FDIC, OCC, Fed, and NCUA examination standards. Full methodology documentation for examiner defensibility.',
  },
  {
    icon: GlobeAltIcon,
    title: 'Regulatory Change Intelligence',
    description:
      'Automated monitoring of enforcement actions and regulatory updates from CFPB, OCC, and FDIC. Enforcement scraping with deduplication, high-value action flagging, compliance area tagging, and priority assignment. Alerts when new guidance or enforcement patterns affect your compliance program.',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'CRA Module',
    description:
      'Assessment area delineation with census tract demographics. Lending Test analysis pulling from HMDA, small business, small farm, and consumer origination data. Community development activity tracking across loans, investments, grants, and services. CRA health scoring, gap analysis for underserved geographies, and exam readiness assessment.',
  },
];

const interconnectivityFlows = [
  {
    title: 'HMDA LAR Data Flows Into Fair Lending',
    description: 'LAR entries are enriched with 50+ derived columns and fed directly into denial rate, pricing, and geographic disparity analyses — no re-upload, no re-entry.',
  },
  {
    title: 'Fair Lending Findings Feed Exam Packages',
    description: 'Statistical findings, risk levels, and affected demographic groups are automatically pulled into one-click examiner-ready ZIP exports with full methodology documentation.',
  },
  {
    title: 'CRA Lending Analysis Uses HMDA Originations',
    description: 'CRA lending tests pull origination volumes and geographic distribution from the same LAR data, with assessment area mapping and income-level classification.',
  },
  {
    title: 'Regression Validates Fair Lending Disparities',
    description: 'Regression findings link to fair lending analyses, providing statistical validation for disparities identified in denial rate and pricing reviews.',
  },
  {
    title: 'Board Reports Aggregate Every Module',
    description: 'Executive-level reports pull marketing compliance stats, fair lending findings, CRA health scores, regression results, and regulatory updates into a single cross-module view.',
  },
];

const clientScenarios = [
  {
    icon: ShieldCheckIcon,
    title: 'Compliance Program Assessments',
    description:
      'When Bates conducts compliance program reviews, Clarid provides the technology layer that operationalizes the recommendations. Consultants can deploy a system that continuously monitors compliance posture and flags gaps — giving clients ongoing execution between engagements.',
  },
  {
    icon: ScaleIcon,
    title: 'Mock Examinations & Remediation',
    description:
      'Clarid generates the same analytical outputs examiners evaluate — HMDA data quality scores, fair lending statistical tests, denial rate disparities, marketing compliance audit trails. Consultants can use these outputs during mock exams and leave clients with ongoing monitoring.',
  },
  {
    icon: UserGroupIcon,
    title: 'Regulatory Action Response',
    description:
      'For clients responding to consent orders or MRAs, Clarid provides documented evidence of corrective action — continuous monitoring, automated testing, and examiner-ready reports that demonstrate sustained compliance improvement, not just point-in-time fixes.',
  },
  {
    icon: ChartBarIcon,
    title: 'Fair Lending & BSA/AML Analytics',
    description:
      'Statistical testing, regression analysis, and risk scoring for fair lending reviews and BSA/AML risk assessments. Automated analysis that would take days manually, delivered with full methodology documentation for examiner defensibility.',
  },
];

const partnershipModels = [
  {
    num: '01',
    title: 'Consulting Enhancement',
    description:
      "Bates consultants deploy Clarid as part of compliance program engagements. The technology layer stays with the client after the engagement, providing continuous monitoring that maintains the standards Bates helped establish. Bates provides the strategic expertise. Clarid provides the ongoing execution.",
  },
  {
    num: '02',
    title: 'Referral & Co-Sell',
    description:
      "Bates recommends Clarid to banking and financial services clients who need continuous compliance monitoring. Clarid handles the technology relationship. Bates maintains the advisory relationship. Both deliver more value — and the client sees Bates as the firm that connected them with best-in-class technology.",
  },
  {
    num: '03',
    title: 'Expert Network Integration',
    description:
      "Clarid's AI generates findings. Bates' 175+ expert consultants validate them. A natural pairing — automated detection with human expert judgment. For complex matters, Clarid clients can access Bates' bench of former regulators, compliance officers, and litigation support professionals.",
  },
];

const dueDiligenceDocs = [
  'AI Governance Policy',
  'FS AI RMF Gap Analysis (230 control objectives mapped, 100% coverage)',
  'Model Inventory with validation framework',
  'Bias Testing Methodology & Results',
  'Data Flow Architecture Documentation',
  'Information Security Program (WISP)',
  'Incident Response Plan',
  'Business Continuity Plan',
  'Vendor Inventory (third-party dependencies)',
];

const nextSteps = [
  { num: 1, title: 'This Conversation', description: "Explore alignment between Bates Group's compliance consulting practice and Clarid's AI-native platform" },
  { num: 2, title: 'Platform Walkthrough', description: 'Live demo of the compliance health dashboard, fair lending analytics, marketing compliance monitoring, and exam packaging' },
  { num: 3, title: 'Joint Client Pilot', description: 'Identify 2–3 Bates banking or fintech clients for a structured pilot with defined success criteria' },
  { num: 4, title: 'Partnership Formalization', description: 'Define the partnership model, referral structure, and go-to-market approach' },
];

/* ─── Component ─── */
export default function BatesPartnerPage() {
  useEffect(() => {
    document.title = 'Bates Group + Clarid — AI-Native Compliance for Financial Services';

    const metaTags: Record<string, string> = {
      'og:title': 'Bates Group + Clarid — Strategic Partnership',
      'og:description': 'AI-native compliance platform for financial institutions. Built by a community bank CMO, with partners from the FDIC and Federal Reserve.',
      'og:type': 'website',
      'og:image': '/clarid-logo.png',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Bates Group + Clarid Partnership',
      'twitter:description': 'Expert advisory meets AI-native compliance technology.',
    };

    const existingTags: HTMLMetaElement[] = [];
    for (const [property, content] of Object.entries(metaTags)) {
      const meta = document.createElement('meta');
      meta.setAttribute('property', property);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
      existingTags.push(meta);
    }

    return () => {
      existingTags.forEach((tag) => document.head.removeChild(tag));
      document.title = 'Clarid — Clarity in every communication';
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-body text-slate-600">
      {/* ─── Minimal Header ─── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 py-5 flex items-center">
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-3">
              <img src="/bates-logo.svg" alt="Bates Group" className="h-10" />
              <span className="text-xl font-display font-bold text-navy-900 tracking-tight">Bates Group</span>
            </div>
            <span className="text-slate-300 text-xl font-light">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-10" />
          </div>
        </div>
      </header>

      {/* ─── Section 1: Hero ─── */}
      <section className="pt-36 pb-24 md:pt-48 md:pb-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeUp>
            <p className="text-base font-semibold tracking-[0.2em] uppercase text-indigo-600 mb-8">
              Strategic Partnership
            </p>
          </FadeUp>

          <FadeUp delay={0.1}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] font-display font-bold text-navy-900 leading-[1.1] mb-10 max-w-5xl mx-auto">
              Expert Advisory Meets AI-Native Compliance
            </h1>
          </FadeUp>

          <FadeUp delay={0.2}>
            <p className="text-xl md:text-2xl lg:text-[1.65rem] text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Bates Group brings 40 years of compliance consulting expertise and 175+ subject matter
              experts across banking, broker-dealer, RIA, and fintech. Clarid brings the AI-native
              compliance platform that operationalizes expert recommendations into continuous, automated
              monitoring. Together — advisory depth with technology that sustains it.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 2: The Compliance Gap ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
                The Gap Between Expert Guidance and Daily Execution
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="space-y-7">
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Financial institutions invest in expert compliance consulting for good reason — program
                  assessments, mock examinations, policy remediation, and regulatory response require deep
                  domain expertise. But the challenge isn't the quality of the guidance. It's the gap
                  between strategic recommendations and the daily operational execution that keeps a
                  compliance program running.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  Community banks and financial institutions with lean compliance teams — often 1 to 3
                  people managing HMDA, fair lending, marketing compliance, CRA, and regulatory change
                  management simultaneously — need technology that automates the execution layer. Not
                  another portal. A system that actually does the work, continuously, with examiner-ready
                  outputs.
                </p>
                <p className="text-xl md:text-[1.35rem] text-slate-600 leading-relaxed">
                  At the same time, AI adoption in financial services is accelerating faster than
                  compliance frameworks can keep up. Microsoft Copilot is generating marketing content.
                  AI tools are touching lending workflows and customer communications. Every new
                  capability creates new compliance exposure — and most institutions don't have a
                  systematic way to manage it.
                </p>
                <p className="text-xl md:text-[1.35rem] font-semibold text-navy-900 mt-10">
                  Bates has the expertise to identify the gaps. Clarid has the technology to close them continuously.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Platform Capabilities ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
              What Clarid Does
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {capabilities.map((cap, i) => (
              <ScaleIn key={cap.title} delay={i * 0.1}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 group-hover:scale-110 transition-all duration-300">
                      <cap.icon className="h-6 w-6 text-indigo-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{cap.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{cap.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.4}>
            <p className="mt-14 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
              Clarid integrates into the tools bankers already use — Microsoft Copilot, Claude, ChatGPT — through
              MCP (Model Context Protocol). No new portal to learn. Compliance intelligence embedded in daily workflows.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 4: Cross-Module Interconnectivity ─── */}
      <section className="py-24 md:py-36 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '48px 48px',
        }} />

        <div className="relative max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <p className="text-base font-semibold tracking-[0.2em] uppercase text-indigo-300 mb-5">
                The Differentiator
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-white mb-8">
                Cross-Module Interconnectivity
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-400 max-w-5xl mx-auto leading-relaxed">
                Most compliance tools are siloed — HMDA validation in one system, fair lending analysis in another,
                marketing compliance somewhere else, board reporting in a spreadsheet. Clarid connects them.
                Marketing compliance findings flow into fair lending risk assessments. HMDA data feeds
                directly into CRA lending tests and regression analysis. Exam packages and board reports
                pull from every module automatically. No one else does this.
              </p>
            </div>
          </FadeUp>

          <div className="space-y-5 lg:space-y-6 mt-14">
            {interconnectivityFlows.map((flow, i) => (
              <ScaleIn key={flow.title} delay={i * 0.08}>
                <div className="p-8 lg:p-10 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/20 transition-all duration-500">
                  <div className="flex items-center gap-4 mb-3">
                    <ArrowsPointingInIcon className="h-6 w-6 text-indigo-400 flex-shrink-0" />
                    <h3 className="text-xl font-display font-bold text-white">{flow.title}</h3>
                  </div>
                  <p className="text-lg text-slate-400 leading-relaxed pl-10">{flow.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>

          <FadeUp delay={0.5}>
            <p className="mt-14 text-center text-lg text-slate-400 max-w-4xl mx-auto leading-relaxed">
              This interconnectivity means a single data upload triggers analysis across multiple compliance
              domains — and the results surface together in exam packages and board reports. For consultants,
              it means the technology layer reinforces every recommendation across the entire compliance program,
              not just one piece of it.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ─── Section 5: How Bates Clients Benefit ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <div className="text-center mb-14">
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 mb-8">
                How Bates Clients Benefit
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 max-w-5xl mx-auto leading-relaxed">
                Clarid complements the work Bates consultants already do — adding a continuous technology
                layer that operationalizes expert recommendations and keeps them running.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {clientScenarios.map((scenario, i) => (
              <ScaleIn key={scenario.title} delay={i * 0.08}>
                <div className="group p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 hover:border-indigo-200 hover:shadow-xl transition-all duration-500 h-full">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-100 group-hover:scale-110 transition-all duration-300">
                      <scenario.icon className="h-6 w-6 text-indigo-600" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-navy-900">{scenario.title}</h3>
                  </div>
                  <p className="text-lg text-slate-500 leading-relaxed">{scenario.description}</p>
                </div>
              </ScaleIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 6: Examination Perspective ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <FadeUp>
            <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-14">
              Built With the Examination Perspective
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            <ScaleIn delay={0.1}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Partners from the FDIC and Federal Reserve
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  A former FDIC Supervisory Examiner and a former Federal Reserve Supervisory
                  Examiner and fair lending subject matter expert — with decades of combined
                  examination experience across hundreds of community bank examinations — are
                  partners in Clarid. Every rule engine, every workflow, every output is validated
                  against what examiners actually evaluate.
                </p>
              </div>
            </ScaleIn>

            <ScaleIn delay={0.2}>
              <div className="p-10 lg:p-12 bg-white rounded-2xl border border-slate-100 hover:shadow-xl transition-all duration-500 h-full">
                <h3 className="text-2xl font-display font-bold text-navy-900 mb-5">
                  Built by the Buyer
                </h3>
                <p className="text-lg text-slate-500 leading-relaxed">
                  Clarid's founder is a community bank CMO who manages marketing compliance, HMDA
                  reporting, and examiner relationships as part of his daily work. This platform
                  wasn't designed by engineers guessing at compliance needs — it was built by someone
                  who lives them and who will sit across from examiners using the outputs Clarid
                  generates.
                </p>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ─── Section 7: Partnership Models ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                How This Works Together
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-16 leading-relaxed">
                Bates Group's compliance consulting expertise combined with Clarid's AI-native
                platform gives financial institutions both expert guidance and real-time technology —
                the advisory framework and the automated execution layer working together.
              </p>
            </FadeUp>

            <div className="space-y-6 lg:space-y-8">
              {partnershipModels.map((model, i) => (
                <ScaleIn key={model.num} delay={i * 0.1}>
                  <div className="p-10 lg:p-12 bg-slate-50 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500">
                    <div className="flex items-start gap-6">
                      <span className="text-3xl font-display font-bold text-navy-900/20 flex-shrink-0 mt-0.5">
                        {model.num}
                      </span>
                      <div>
                        <h3 className="text-2xl font-display font-bold text-navy-900 mb-3">{model.title}</h3>
                        <p className="text-lg text-slate-500 leading-relaxed">{model.description}</p>
                      </div>
                    </div>
                  </div>
                </ScaleIn>
              ))}
            </div>

            <FadeUp delay={0.4}>
              <p className="mt-12 text-center text-lg text-slate-500 italic">
                We'd welcome a conversation about which model — or combination — aligns best with
                Bates Group's client strategy and practice areas.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 8: Due Diligence ─── */}
      <section className="py-24 md:py-36 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-5xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-6">
                We've Done the Homework
              </h2>
              <p className="text-xl md:text-[1.35rem] text-slate-500 text-center max-w-4xl mx-auto mb-14 leading-relaxed">
                Clarid maintains a comprehensive vendor due diligence package designed to meet the
                expectations outlined in interagency guidance on third-party relationships. Key
                documentation includes:
              </p>
            </FadeUp>

            <FadeUp delay={0.1}>
              <div className="max-w-3xl mx-auto">
                <ul className="space-y-5">
                  {dueDiligenceDocs.map((doc, i) => (
                    <FadeIn key={doc} delay={0.15 + i * 0.05}>
                      <li className="flex items-start gap-4">
                        <CheckCircleIcon className="h-6 w-6 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <span className="text-lg text-slate-600">{doc}</span>
                      </li>
                    </FadeIn>
                  ))}
                </ul>
              </div>
            </FadeUp>

            <FadeUp delay={0.6}>
              <p className="mt-12 text-center text-lg text-slate-500 max-w-4xl mx-auto leading-relaxed">
                Our Executive Due Diligence Summary, Data Flow Architecture, and full FS AI RMF gap
                analysis are available for review. SOC 2 Type I is on our 12-month roadmap — we're
                documenting controls now.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ─── Section 9: Moving Forward ─── */}
      <section className="py-24 md:py-36">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <FadeUp>
              <h2 className="text-4xl md:text-5xl lg:text-[3.25rem] font-display font-bold text-navy-900 text-center mb-16">
                Moving Forward
              </h2>
            </FadeUp>

            <div className="space-y-10 lg:space-y-12">
              {nextSteps.map((step, i) => (
                <FadeUp key={step.num} delay={i * 0.12}>
                  <div className="flex items-start gap-7">
                    <div className="flex-shrink-0 w-14 h-14 rounded-full bg-navy-900 text-white flex items-center justify-center text-lg font-bold">
                      {step.num}
                    </div>
                    <div className="pt-2">
                      <h3 className="text-xl md:text-2xl font-display font-bold text-navy-900 mb-2">{step.title}</h3>
                      <p className="text-lg text-slate-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 10: Contact ─── */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 text-center">
          <FadeIn>
            <div className="space-y-3">
              <p className="text-3xl font-display font-bold text-navy-900">Bobby Sloan</p>
              <p className="text-lg text-slate-500">Founder & CEO, Clarid</p>
              <div className="flex items-center justify-center gap-3 mt-4 text-lg text-indigo-600">
                <EnvelopeIcon className="h-5 w-5" />
                <a href="mailto:bobby@clarid.ai" className="hover:text-indigo-700 transition-colors">
                  bobby@clarid.ai
                </a>
              </div>
              <p className="text-base text-slate-400 mt-1">clarid.ai</p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── Minimal Footer ─── */}
      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-[90rem] mx-auto px-8 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/bates-logo.svg" alt="Bates Group" className="h-7 opacity-40" />
            <span className="text-slate-200">|</span>
            <img src="/clarid-logo.png" alt="Clarid" className="h-7 opacity-40" />
          </div>
          <p className="text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} Clarid. Confidential.
          </p>
        </div>
      </footer>
    </div>
  );
}
